import * as Sentry from "@sentry/react";

// Initialize Sentry as early as possible
Sentry.init({
  dsn: "https://b5bbdba8934fddd5c919aa0a1e50c171@o4509547778408448.ingest.de.sentry.io/4509570784559184",
  sendDefaultPii: true,
  
  // Performance monitoring
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["error", "warn"] }),
    Sentry.feedbackIntegration({
      colorScheme: "light",
      showBranding: false,
      autoInject: false,
      enableScreenshot: false,
      isEmailRequired: true,
    }),
  ],
  
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost", 
    /^https:\/\/www\.postpark-winsen\.de/, 
    /^https:\/\/immersive-archviz-dev\.bizzlogic\.com/, 
    /^https:\/\/www\.plaza\.bizzlogic\.com/
  ],
  
  // Enhanced logging
  _experiments: {
    enableLogs: true,
  },
  
  // Environment detection
  environment: import.meta.env.DEV ? 'development' : 'production',
  
  // Error filtering for development
  beforeSend(event: any) {
    if (import.meta.env.DEV && event.exception?.values?.[0]?.value?.includes('HMR')) {
      return null;
    }
    return event;
  },
  
  // Custom context
  initialScope: {
    tags: {
      component: "plaza_framework",
      integration: "pixelstreaming"
    }
  }
});

import { BannerComponent, BannerRef } from './components/BannerComponent';
import { registerReactComponentAsCustomElement } from './utils/registerCustomElement';
import bannerCSS from './components/BannerComponent.css?raw'; // Import Banner CSS as raw string
import uiLayerCSS from './components/UILayerComponent.css?raw'; // Import UI Layer CSS as raw string
import globalCSS from './styles.css?raw'; // Import global CSS as raw string

// Debugging
console.log('PlazaBanner: Script loaded');

// Export components for direct use
export { BannerComponent } from './components/BannerComponent';
export type { BannerRef } from './components/BannerComponent';
export { PixelStreamingHandler } from './sdk/PixelStreamingHandler';

// Define the observed attributes for the custom element
const OBSERVED_ATTRIBUTES = [
  'share-id',
  'title',
  'description',
  'button-text',
  'class-name',
  'initial-settings',
  'ui-settings',
  'trigger-ref'
];

// Combine CSS strings
const combinedCSS = globalCSS + '\n' + bannerCSS + '\n' + uiLayerCSS;

// Register the custom element
console.log('PlazaBanner: Registering custom element');
registerReactComponentAsCustomElement(BannerComponent, {
  tagName: 'plaza-banner',
  observedAttributes: OBSERVED_ATTRIBUTES,
  cssString: combinedCSS // Pass the combined CSS string
});
console.log('PlazaBanner: Custom element registered');

// Helper to inherit CSS variables from document to custom element
function inheritCssVariables() {
  const computedStyle = window.getComputedStyle(document.documentElement);
  const customElements = document.querySelectorAll('plaza-banner');
  
  customElements.forEach(element => {
    // Copy CSS variables from :root to the custom element
    for (let i = 0; i < computedStyle.length; i++) {
      const prop = computedStyle[i];
      if (prop.startsWith('--')) {
        (element as HTMLElement).style.setProperty(prop, computedStyle.getPropertyValue(prop));
      }
    }
  });
}

// Set up MutationObserver to detect when custom elements are added
if (typeof window !== 'undefined') {
  // Initial inheritance when DOM is ready
  document.addEventListener('DOMContentLoaded', inheritCssVariables);
  
  // Set up observer for future elements
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        inheritCssVariables();
      }
    }
  });
  
  // Start observing when document is ready
  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, { childList: true, subtree: true });
  });
}

// Map to store banner controller references
const bannerControllers = new Map<HTMLElement, BannerRef>();

// A utility function to initialize and mount the banner programmatically
export function createBanner(container: HTMLElement, props: any) {
  console.log('PlazaBanner: createBanner called with props:', props);
  
  const element = document.createElement('plaza-banner');
  
  // Set attributes based on props
  if (props.shareId) {
    console.log('PlazaBanner: Setting share-id attribute:', props.shareId);
    element.setAttribute('share-id', props.shareId);
  }
  
  if (props.title) {
    element.setAttribute('title', props.title);
  }
  
  if (props.description) {
    element.setAttribute('description', props.description);
  }
  
  if (props.buttonText) {
    element.setAttribute('button-text', props.buttonText);
  }
  
  if (props.className) {
    element.setAttribute('class-name', props.className);
  }
  
  // Handle complex objects by JSON stringifying them
  if (props.initialSettings) {
    console.log('PlazaBanner: Setting initial-settings:', props.initialSettings);
    element.setAttribute('initial-settings', JSON.stringify(props.initialSettings));
  }
  
  if (props.uiSettings) {
    console.log('PlazaBanner: Setting ui-settings:', props.uiSettings);
    element.setAttribute('ui-settings', JSON.stringify(props.uiSettings));
  }
  
  // Append to container
  console.log('PlazaBanner: Appending to container');
  container.appendChild(element);
  
  return element;
}

// Creates a banner and returns a controller to interact with it
export function createControlledBanner(container: HTMLElement, props: any): {
  element: HTMLElement;
  controller: {
    startExperience: () => Promise<void>;
  }
} {
  console.log('PlazaBanner: createControlledBanner called with props:', props);
  
  // Create a ref to store the banner controller
  const bannerRef: { current: BannerRef | null } = { current: null };
  
  // Create element
  const element = document.createElement('plaza-banner');
  
  // Store the ref in a custom property on the element
  (element as any)._bannerRef = bannerRef;
  
  // Set attributes based on props
  if (props.shareId) {
    element.setAttribute('share-id', props.shareId);
  }
  
  if (props.title) {
    element.setAttribute('title', props.title);
  }
  
  if (props.description) {
    element.setAttribute('description', props.description);
  }
  
  if (props.buttonText) {
    element.setAttribute('button-text', props.buttonText);
  }
  
  if (props.className) {
    element.setAttribute('class-name', props.className);
  }
  
  // Handle complex objects by JSON stringifying them
  if (props.initialSettings) {
    element.setAttribute('initial-settings', JSON.stringify(props.initialSettings));
  }
  
  if (props.uiSettings) {
    element.setAttribute('ui-settings', JSON.stringify(props.uiSettings));
  }
  
  // Set the triggerRef attribute to signal the component to connect the ref
  element.setAttribute('trigger-ref', 'true');
  
  // Append to container
  container.appendChild(element);
  
  // Create a controller object that will interact with the banner
  const controller = {
    startExperience: async () => {
      if (bannerRef.current) {
        await bannerRef.current.startExperience();
      } else {
        console.error('PlazaBanner: Banner reference not available yet');
      }
    }
  };
  
  // Store the controller for later use
  bannerControllers.set(element, bannerRef as unknown as BannerRef);
  
  return {
    element,
    controller
  };
}

// Add a function to start the experience for an existing banner element
export function startBannerExperience(bannerElement: HTMLElement): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const bannerRef = bannerControllers.get(bannerElement);
    
    if (bannerRef && bannerRef.startExperience) {
      bannerRef.startExperience().then(resolve).catch(reject);
    } else {
      console.error('PlazaBanner: Cannot start experience - no controller found for this banner element');
      reject(new Error('No controller found for this banner element'));
    }
  });
}

// Add a global access point when loaded as a UMD bundle
declare global {
  interface Window {
    PlazaBanner: {
      create: typeof createBanner;
      createControlled: typeof createControlledBanner;
      startExperience: typeof startBannerExperience;
    };
  }
}

// Expose a global API if loaded directly as a script
if (typeof window !== 'undefined') {
  console.log('PlazaBanner: Exposing global API');
  window.PlazaBanner = {
    create: createBanner,
    createControlled: createControlledBanner,
    startExperience: startBannerExperience
  };
} 